// Status dots of the sidebar (HTML DS2 shell RULES): an input panel is "bad" when a visible enabled field breaks its
// min/max, pending (null) while a visible enabled number field or select is empty, "ok" otherwise; the results link
// follows the global verdict.
import type { ControlValues, UiState } from "../../../http/screwConnector/formulas";
import type { DotStatus, InputSectionId } from "./types";

type Check = { value: number | string; shown: boolean; disabled?: boolean; min?: number | null; max?: number | null };

function statusOf(checks: Check[]): DotStatus {
  const active = checks.filter((c) => c.shown && !c.disabled);
  const outOfRange = (c: Check) =>
    typeof c.value === "number" &&
    Number.isFinite(c.value) &&
    ((c.min != null && c.value < c.min) || (c.max != null && c.value > c.max));
  if (active.some(outOfRange)) return "bad";
  const empty = (c: Check) => (typeof c.value === "number" ? !Number.isFinite(c.value) : c.value.trim() === "");
  return active.some(empty) ? null : "ok";
}

function elementChecks(n: 1 | 2, v: ControlValues, ui: UiState): Check[] {
  const vis = ui.visible;
  const dis = ui.disabled;
  const normal = vis[`mat${n}NormalFields` as const];
  const clt = vis[`mat${n}CltFields` as const];
  const userLayers = clt && vis[`clt${n}UserLayersWrap` as const];
  const checks: Check[] = [
    { value: v[`mat${n}Tipo` as const], shown: true },
    {
      value: v[`mat${n}Specie` as const],
      shown: normal && vis[`mat${n}SpecieClasseRow` as const] && (n === 2 || vis.mat1SpecieField),
    },
    { value: v[`mat${n}Classe` as const], shown: normal && vis[`mat${n}SpecieClasseRow` as const] },
    { value: v[`clt${n}Lato` as const], shown: clt },
    { value: v[`clt${n}NumStrati` as const], shown: clt },
    { value: v[`clt${n}Disposizione` as const], shown: clt },
    { value: v[`clt${n}SpessoriMode` as const], shown: clt && vis[`clt${n}SpessoriModeField` as const] },
    { value: v[`clt${n}Spessore0` as const], shown: clt && vis[`clt${n}Spessore0RowField` as const], min: 1 },
    {
      value: v[`clt${n}Spessore90` as const],
      shown: clt && vis[`clt${n}Spessore0RowField` as const] && vis[`clt${n}Spessore90Field` as const],
      min: 1,
    },
    { value: v[`clt${n}Classe` as const], shown: clt && vis[`clt${n}ClasseField` as const] },
    ...v[`clt${n}UserLayers` as const].flatMap((l): Check[] => [
      { value: l.spessore, shown: userLayers, min: 1 },
      { value: l.classe, shown: userLayers },
    ]),
    { value: v[`fi${n}` as const], shown: vis[`fi${n}Field` as const] },
    { value: v[`eps${n}` as const], shown: vis[`eps${n}Field` as const], disabled: dis[`eps${n}` as const], min: 0, max: 90 },
    { value: v[`b${n}` as const], shown: true, disabled: n === 2 && dis.b2 },
    {
      value: v[`d${n}` as const],
      shown: vis[`d${n}Field` as const],
      disabled: n === 2 && dis.d2,
      min: n === 1 ? ui.limits.d1Min : ui.limits.d2Min,
    },
    { value: v[`spessore${n}` as const], shown: true, disabled: dis[`spessore${n}` as const] },
    { value: v[`rhok${n}` as const], shown: n === 2 || vis.rhok1Field, disabled: dis[`rhok${n}` as const] },
    { value: v[`rhomean${n}` as const], shown: n === 2 || vis.rhomean1Row },
    { value: v[`a4t${n}` as const], shown: true, disabled: dis[`a4t${n}` as const] },
    { value: v[`a4c${n}` as const], shown: true, disabled: dis[`a4c${n}` as const] },
  ];
  if (n === 1) checks.push({ value: v.a3t1, shown: true, disabled: dis.a3t1 });
  else checks.push({ value: v.a3t2, shown: true, disabled: dis.a3t2 }, { value: v.a3c2, shown: true, disabled: dis.a3c2 });
  return checks;
}

export function sectionStatuses(v: ControlValues | null, ui: UiState): Record<InputSectionId, DotStatus> {
  if (!v) {
    return { secTipologia: null, secEl1: null, secEl2: null, secCarichi: null, secConnessione: null, secGriglia: null };
  }
  const vis = ui.visible;
  const dis = ui.disabled;
  return {
    secTipologia: statusOf([{ value: v.thetaNum, shown: true, min: 1, max: 180 }]),
    secEl1: statusOf(elementChecks(1, v, ui)),
    secEl2: statusOf(elementChecks(2, v, ui)),
    secCarichi: statusOf([
      { value: v.forceFn, shown: true },
      { value: v.forceFv, shown: true },
      { value: v.forceFa, shown: true },
      { value: v.classeServizio, shown: true },
      { value: v.durataCarico, shown: true },
      { value: v.normativaGammaM, shown: true },
      { value: v.gammaMColonna, shown: vis.gammaMColonnaWrap },
      { value: v.gammaM, shown: true, disabled: dis.gammaM },
    ]),
    secConnessione: statusOf([
      { value: v.etaForo, shown: true },
      { value: v.etaDiametro, shown: true },
      { value: v.etaLunghezza, shown: true },
      { value: v.rondellaCodice, shown: vis.rondellaField && vis.rondellaCodice },
    ]),
    secGriglia: statusOf([
      { value: v.rows, shown: true, min: 1, max: ui.limits.rowsMax },
      { value: v.cols, shown: true, min: 1, max: ui.limits.colsMax },
      { value: v.s11, shown: true, disabled: dis.s11 },
      { value: v.s12, shown: true, disabled: dis.s12 },
      { value: v.centroOrizz, shown: true },
      { value: v.centroVert, shown: true },
    ]),
  };
}

// results link / page badge: In attesa dei dati / Verificato / Non verificato
export function resultsStatus(ui: UiState, blockReason: string | null): DotStatus {
  const status = ui.results?.status ?? "waiting";
  if (blockReason || status === "waiting") return null;
  return status;
}
